import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
    CommonTenantRequest,
    UpsertTenantAdminAndCreatorRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/api/tenantAdminAndCreator.api.ts';
import type {
    TenantAdminAndCreatorItem,
    TenantAdminAndCreatorSearchResponse,
    TenantAdminAndCreatorUsersSearchResponse,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/api/tenantAdminAndCreator.api.ts';
import {
    createApiTenantAdminAndCreator,
    deleteApiTenantAdminAndCreator,
    getApiTenantAdminAndCreator,
    getApiTenantAdminAndCreatorUsers,
    updateApiTenantAdminAndCreator,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/api/tenantAdminAndCreator.api.ts';
import { extractError } from '@utils/slice.ts';

interface TenantAdminError {
    message?: string;
    [key: string]: any;
}

interface TenantAdminState {
    error: TenantAdminError | null;
    tenantAdminsData: TenantAdminAndCreatorSearchResponse | null;
    tenantAdminUsersData: TenantAdminAndCreatorUsersSearchResponse | null;

    isGetTenantAdminsPending: boolean;
    isCreateTenantAdminPending: boolean;
    isUpdateTenantAdminPending: boolean;
    isDeleteTenantAdminPending: boolean;
    isGetTenantAdminUsersPending: boolean;
}

const initialState: TenantAdminState = {
    error: null,
    tenantAdminsData: null,
    tenantAdminUsersData: null,

    isGetTenantAdminsPending: false,
    isCreateTenantAdminPending: false,
    isUpdateTenantAdminPending: false,
    isDeleteTenantAdminPending: false,
    isGetTenantAdminUsersPending: false,
};

export const getTenantAdmins = createAsyncThunk<
    TenantAdminAndCreatorSearchResponse,
    { tenantAlias: string; payload: CommonTenantRequest },
    { rejectValue: TenantAdminError }
>('tenantAdminRoles/getTenantAdmins', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiTenantAdminAndCreator(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createTenantAdmin = createAsyncThunk<
    TenantAdminAndCreatorItem,
    { tenantAlias: string; payload: UpsertTenantAdminAndCreatorRequest },
    { rejectValue: TenantAdminError }
>('tenantAdminRoles/createTenantAdmin', ({ tenantAlias, payload }, { rejectWithValue }) =>
    createApiTenantAdminAndCreator(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateTenantAdmin = createAsyncThunk<
    TenantAdminAndCreatorItem,
    { tenantAlias: string; accessId: number; payload: UpsertTenantAdminAndCreatorRequest },
    { rejectValue: TenantAdminError }
>('tenantAdminRoles/updateTenantAdmin', ({ tenantAlias, accessId, payload }, { rejectWithValue }) =>
    updateApiTenantAdminAndCreator(tenantAlias, accessId, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteTenantAdmin = createAsyncThunk<
    void,
    { tenantAlias: string; accessId: number },
    { rejectValue: TenantAdminError }
>('tenantAdminRoles/deleteTenantAdmin', ({ tenantAlias, accessId }, { rejectWithValue }) =>
    deleteApiTenantAdminAndCreator(tenantAlias, accessId)
        .then(() => undefined)
        .catch((err) => rejectWithValue(err)),
);

export const getTenantAdminUsers = createAsyncThunk<
    TenantAdminAndCreatorUsersSearchResponse,
    { tenantAlias: string; payload: CommonTenantRequest },
    { rejectValue: TenantAdminError }
>('tenantAdminRoles/getTenantAdminUsers', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiTenantAdminAndCreatorUsers(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const tenantAdminSlice = createSlice({
    name: 'tenantAdminRoles',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getTenantAdmins.pending, (state) => {
            state.isGetTenantAdminsPending = true;
            state.error = null;
        });
        builder.addCase(getTenantAdmins.fulfilled, (state, action) => {
            state.isGetTenantAdminsPending = false;
            state.tenantAdminsData = action.payload;
        });
        builder.addCase(getTenantAdmins.rejected, (state, action) => {
            state.isGetTenantAdminsPending = false;
            state.error = extractError(action);
        });

        builder.addCase(createTenantAdmin.pending, (state) => {
            state.isCreateTenantAdminPending = true;
            state.error = null;
        });
        builder.addCase(createTenantAdmin.fulfilled, (state) => {
            state.isCreateTenantAdminPending = false;
        });
        builder.addCase(createTenantAdmin.rejected, (state, action) => {
            state.isCreateTenantAdminPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateTenantAdmin.pending, (state) => {
            state.isUpdateTenantAdminPending = true;
            state.error = null;
        });
        builder.addCase(updateTenantAdmin.fulfilled, (state) => {
            state.isUpdateTenantAdminPending = false;
        });
        builder.addCase(updateTenantAdmin.rejected, (state, action) => {
            state.isUpdateTenantAdminPending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteTenantAdmin.pending, (state) => {
            state.isDeleteTenantAdminPending = true;
            state.error = null;
        });
        builder.addCase(deleteTenantAdmin.fulfilled, (state) => {
            state.isDeleteTenantAdminPending = false;
        });
        builder.addCase(deleteTenantAdmin.rejected, (state, action) => {
            state.isDeleteTenantAdminPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getTenantAdminUsers.pending, (state) => {
            state.isGetTenantAdminUsersPending = true;
            state.error = null;
        });
        builder.addCase(getTenantAdminUsers.fulfilled, (state, action) => {
            state.isGetTenantAdminUsersPending = false;
            state.tenantAdminUsersData = action.payload;
        });
        builder.addCase(getTenantAdminUsers.rejected, (state, action) => {
            state.isGetTenantAdminUsersPending = false;
            state.error = extractError(action);
        });
    },
});

export default tenantAdminSlice.reducer;
